// cm-coding: Editing Workday connector settings must not call Workday or import
// requisitions. This hook stores and validates form choices only; explicit pull
// actions create background transfer jobs after the settings have been saved.

import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'

import type { NodeApiClient } from '@/app-shell/api/node-api-client'
import type {
  ConnectorCredentialSource,
  ConnectorFeedFor,
} from '../../types'
import {
  createWorkdayFeed,
  deleteWorkdayConnection,
  getWorkdayConnection,
  saveWorkdayConnection,
  updateWorkdayFeed,
} from '../services/api'
import type {
  WorkdayConnectionStatus,
  WorkdayFeedOptions,
  WorkdayFilter,
  WorkdayWorkspaceCredentials,
} from '../types'
import {
  WORKDAY_DEFAULT_API_URL,
  WORKDAY_DEFAULT_FILTERS,
  WORKDAY_SOURCE_DISPLAY_FIELDS,
} from '../types'

const EMPTY_CREDENTIALS: WorkdayWorkspaceCredentials = { username: '', password: '' }

export function useWorkdayFeedForm({
  api,
  orgId,
  vaultId,
  feed,
  open,
  canAdministerWorkspace,
  onConfigured,
}: {
  api: NodeApiClient
  orgId: string
  vaultId: string
  feed?: ConnectorFeedFor<'workday'> | null
  open: boolean
  canAdministerWorkspace: boolean
  onConfigured: () => Promise<void>
}) {
  const [connection, setConnection] = useState<WorkdayConnectionStatus>({
    workspaceConfigured: null,
    umdConfigured: null,
    umdCredentialKeys: [],
  })
  const [credentialSource, setCredentialSource] =
    useState<ConnectorCredentialSource>('umd')
  const [credentials, setCredentials] =
    useState<WorkdayWorkspaceCredentials>(EMPTY_CREDENTIALS)
  const [apiUrl, setApiUrl] = useState(WORKDAY_DEFAULT_API_URL)
  const [filters, setFilters] = useState<WorkdayFilter[]>(WORKDAY_DEFAULT_FILTERS)
  const [sourceDisplayFieldKeys, setSourceDisplayFieldKeys] = useState<string[]>(
    WORKDAY_SOURCE_DISPLAY_FIELDS.filter(field => field.default).map(field => field.key),
  )
  const [loadingConnection, setLoadingConnection] = useState(false)
  const [saving, setSaving] = useState(false)
  const editing = Boolean(feed)

  const workday = useMemo<WorkdayFeedOptions>(() => ({
    apiUrl: apiUrl.trim(),
    credentialSource,
    filters,
  }), [apiUrl, credentialSource, filters])

  useEffect(() => {
    if (!open) return
    const saved = feed?.workday
    setCredentialSource(saved?.credentialSource ?? 'umd')
    setCredentials(EMPTY_CREDENTIALS)
    setApiUrl(saved?.apiUrl ?? WORKDAY_DEFAULT_API_URL)
    setFilters(saved?.filters ?? WORKDAY_DEFAULT_FILTERS)
    setSourceDisplayFieldKeys(
      feed?.sourceDisplayFieldKeys
      ?? (canAdministerWorkspace
        ? WORKDAY_SOURCE_DISPLAY_FIELDS.filter(field => field.default).map(field => field.key)
        : []),
    )
    setLoadingConnection(true)
    void getWorkdayConnection(api, orgId, vaultId)
      .then(setConnection)
      .catch(() => toast.error('Could not read the Workday connection'))
      .finally(() => setLoadingConnection(false))
  }, [api, canAdministerWorkspace, feed, open, orgId, vaultId])

  const submit = async (): Promise<boolean> => {
    if (
      !workday.apiUrl
      || workday.filters.length < 1
      || workday.filters.some(filter => !filter.value)
    ) {
      toast.error('Complete the Workday requisition scope first')
      return false
    }
    setSaving(true)
    try {
      if (
        credentialSource === 'workspace'
        && credentials.username
        && credentials.password
      ) {
        await saveWorkdayConnection(api, orgId, vaultId, credentials)
        setConnection(current => ({ ...current, workspaceConfigured: true }))
        setCredentials(EMPTY_CREDENTIALS)
      }
      const request = {
        initialSync: 'all' as const,
        schedule: 'manual' as const,
        sourceDisplayFieldKeys,
        workday,
      }
      await (feed
        ? updateWorkdayFeed(
            api,
            orgId,
            vaultId,
            feed.feedId,
            'manual',
            sourceDisplayFieldKeys,
            workday,
          )
        : createWorkdayFeed(api, orgId, vaultId, request))
      // cm-coding: A successful feed mutation is the durable completion
      // boundary. Connector cards and dashboards are presentation consumers;
      // their refresh must not leave an administrator trapped in a saved
      // editor when either query is slow.
      void onConfigured().catch(() => toast.error(
        'Workday source was saved, but connected views could not refresh',
      ))
      toast.success(editing ? 'Workday source updated' : 'Workday source connected')
      return true
    } catch (error: any) {
      toast.error(
        error?.response?.data?.detail
        ?? error?.message
        ?? 'Could not save the Workday source',
      )
      return false
    } finally {
      setSaving(false)
    }
  }

  const deleteCredential = async () => {
    setSaving(true)
    try {
      await deleteWorkdayConnection(api, orgId, vaultId)
      setConnection(current => ({ ...current, workspaceConfigured: false }))
      setCredentials(EMPTY_CREDENTIALS)
    } catch {
      toast.error('Could not delete the Workday credential')
    } finally {
      setSaving(false)
    }
  }

  return {
    apiUrl, setApiUrl,
    connection,
    credentialSource, setCredentialSource,
    credentials, setCredentials,
    filters, setFilters,
    sourceDisplayFieldKeys, setSourceDisplayFieldKeys,
    sourceDisplayFields: WORKDAY_SOURCE_DISPLAY_FIELDS,
    loadingConnection, saving, editing,
    submit, deleteCredential,
  }
}
